// Community Screen - Main community interface with topics, threads, and discussions

import React, { useState, useEffect, useCallback } from 'react';
import { View, ScrollView, RefreshControl } from 'react-native';
import { YStack, XStack, H2, Button, SizableText, Input } from 'tamagui';
import { MaterialIcons, MaterialCommunityIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useDeviceType } from '@hooks/useDeviceType';

// Import components
import TopicsList from './Components/TopicsList';
import ThreadsList from './Components/ThreadsList';
import ThreadDetail from './Components/ThreadDetail';
import CreateTopicModal from './Components/CreateTopicModal';
import CreateThreadModal from './Components/CreateThreadModal';

// Import API
import communityApi from '@api/communityApi';

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Community Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      console.error('Community Error:', this.state.error);
      return (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <SizableText>Something went wrong. Please refresh the page.</SizableText>
        </View>
      );
    }
    return this.props.children;
  }
}

type ViewMode = 'topics' | 'threads' | 'thread';

const Community: React.FC = () => {
  const theme = themeSelector();
  const { isTablet } = useDeviceType();

  // State
  const [viewMode, setViewMode] = useState<ViewMode>('topics');
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [selectedThread, setSelectedThread] = useState<string | null>(null);
  const [showCreateTopic, setShowCreateTopic] = useState(false);
  const [showCreateThread, setShowCreateThread] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Data states
  const [topics, setTopics] = useState<any[]>([]);
  const [threads, setThreads] = useState<any[]>([]);
  const [currentThread, setCurrentThread] = useState<any>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<any>({ topics: [] });
  const [searchLoading, setSearchLoading] = useState(false);

  // UI Configuration
  const isSidebarMode = isTablet; // Use sidebar for tablets/desktop
  const sidebarWidth = 320;

  const colors = {
    background: theme ? '#ffffff' : '#1f2937',
    cardBackground: theme ? '#f8fafc' : '#374151',
    sidebarBackground: theme ? '#f9fafb' : '#374151',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    border: theme ? '#e5e7eb' : '#4b5563',
    primary: '#3b82f6',
    success: '#10b981',
    accent: '#3b82f6',
  };

  // Load topics
  const loadTopics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await communityApi.getTopics();
      console.log(" Loaded topics:", response);
      setTopics(response?.data?.topics);
    } catch (error) {
      console.error('Error loading topics:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load threads for a topic
  const loadThreads = useCallback(async (topicId: string) => {
    try {
      setLoading(true);
      const response = await communityApi.getThreads({ topicId });
      setThreads(response.threads);
    } catch (error) {
      console.error('Error loading threads:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load thread details
  const loadThread = useCallback(async (threadId: string) => {
    try {
      setLoading(true);
      const thread = await communityApi.getThread(threadId);
      const commentsResponse = await communityApi.getComments(threadId);
      setCurrentThread(thread);
      setComments(commentsResponse.comments);
    } catch (error) {
      console.error('Error loading thread:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search functionality
  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults({ topics: [] });
      return;
    }

    try {
      setSearchLoading(true);
      const results = await communityApi.searchCommunity({ query });
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTopics();
  }, [loadTopics]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchQuery, performSearch]);

  const handleTopicSelect = useCallback((topicId: string) => {
    setSelectedTopic(topicId);
    setViewMode('threads');
    loadThreads(topicId);
  }, [loadThreads]);

  const handleThreadSelect = useCallback((threadId: string) => {
    setSelectedThread(threadId);
    setViewMode('thread');
    loadThread(threadId);
  }, [loadThread]);

  const handleBackToTopics = useCallback(() => {
    setSelectedTopic(null);
    setSelectedThread(null);
    setViewMode('topics');
  }, []);

  const handleBackToThreads = useCallback(() => {
    setSelectedThread(null);
    setViewMode('threads');
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (viewMode === 'topics') {
        await loadTopics();
      } else if (viewMode === 'threads' && selectedTopic) {
        await loadThreads(selectedTopic);
      } else if (viewMode === 'thread' && selectedThread) {
        await loadThread(selectedThread);
      }
    } finally {
      setRefreshing(false);
    }
  }, [viewMode, selectedTopic, selectedThread, loadTopics, loadThreads, loadThread]);

  const handleCreateTopic = useCallback(async (data: any) => {
    try {
      await communityApi.createTopic(data);
      setShowCreateTopic(false);
      await loadTopics();
    } catch (error) {
      console.error('Error creating topic:', error);
    }
  }, [loadTopics]);

  const handleCreateThread = useCallback(async (data: any) => {
    try {
      if (selectedTopic) {
        await communityApi.createThread({ ...data, topicId: selectedTopic });
        setShowCreateThread(false);
        await loadThreads(selectedTopic);
      }
    } catch (error) {
      console.error('Error creating thread:', error);
    }
  }, [selectedTopic, loadThreads]);

  const handleCreateSuccess = useCallback(() => {
    setShowCreateTopic(false);
    setShowCreateThread(false);
    if (viewMode === 'topics') {
      loadTopics();
    } else if (viewMode === 'threads' && selectedTopic) {
      loadThreads(selectedTopic);
    }
  }, [viewMode, selectedTopic, loadTopics, loadThreads]);

  // Mobile/Tablet Layout
  if (!isSidebarMode) {
    return (
      <ErrorBoundary>
        <View style={{ flex: 1 }}>
          {/* Header */}
          <YStack
            padding="$4"
            borderBottomWidth={1}
            borderBottomColor={colors.border}
          >
            <XStack justifyContent="space-between" alignItems="center">
              {viewMode !== 'topics' && (
                <Button
                  size="$3"
                  chromeless
                  onPress={viewMode === 'thread' ? handleBackToThreads : handleBackToTopics}
                  icon={<MaterialIcons name="arrow-back" size={20} color={colors.text} />}
                >
                  Back
                </Button>
              )}

              <H2 color={colors.text}>
                {viewMode === 'topics' ? 'Community Topics' :
                  viewMode === 'threads' ? 'Threads' : 'Discussion'}
              </H2>

              {viewMode === 'topics' && (
                <Button
                  size="$3"
                  backgroundColor={colors.primary}
                  onPress={() => setShowCreateTopic(true)}
                  icon={<MaterialIcons name="add" size={16} color="white" />}
                >
                  Topic
                </Button>
              )}

              {viewMode === 'threads' && (
                <Button
                  size="$3"
                  backgroundColor={colors.success}
                  onPress={() => setShowCreateThread(true)}
                  icon={<MaterialIcons name="add" size={16} color="white" />}
                >
                  Thread
                </Button>
              )}
            </XStack>

            {/* Search Bar */}
            {viewMode === 'topics' && (
              <XStack marginTop="$3" alignItems="center" gap="$2">
                <Input
                  flex={1}
                  placeholder="Search community..."
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  backgroundColor={colors.background}
                />
                <MaterialIcons name="search" size={24} color={colors.textSecondary} />
              </XStack>
            )}
          </YStack>

          {/* Content */}
          <ScrollView
            style={{ flex: 1 }}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
            }
          >
            {viewMode === 'topics' && (
              <TopicsList
                topics={searchQuery ? searchResults.topics : topics}
                loading={searchQuery ? searchLoading : loading}
                onTopicSelect={handleTopicSelect}
                selectedTopic={selectedTopic}
              />
            )}

            {viewMode === 'threads' && selectedTopic && (
              <ThreadsList
                threads={threads}
                loading={loading}
                onThreadSelect={handleThreadSelect}
                selectedThread={selectedThread}
                topicTitle={topics?.find(t => t._id === selectedTopic)?.title}
              />
            )}

            {viewMode === 'thread' && selectedThread && (
              <ThreadDetail
                thread={currentThread}
                comments={comments}
                loading={loading}
                onBack={handleBackToThreads}
                onLike={(threadId) => console.log('Like thread:', threadId)}
                onReply={async (content, parentId) => {
                  try {
                    if (selectedThread) {
                      await communityApi.createComment({
                        content,
                        threadId: selectedThread,
                        parentId,
                      });
                      await loadThread(selectedThread);
                    }
                  } catch (error) {
                    console.error('Error creating comment:', error);
                  }
                }}
                currentUserId="currentUser"
              />
            )}
          </ScrollView>

          {/* Modals */}
          <CreateTopicModal
            visible={showCreateTopic}
            onClose={() => setShowCreateTopic(false)}
            onSubmit={handleCreateTopic}
          />

          <CreateThreadModal
            visible={showCreateThread}
            onClose={() => setShowCreateThread(false)}
            onSubmit={handleCreateThread}
            topicTitle={topics?.find(t => t._id === selectedTopic)?.title}
          />
        </View>
      </ErrorBoundary>
    );
  }

  // Desktop/Web Sidebar Layout
  return (
    <ErrorBoundary>
      <XStack flex={1} backgroundColor={colors.background}>
        {/* Left Sidebar - Topics */}
        <YStack
          width={sidebarWidth}
          backgroundColor={colors.sidebarBackground}
          borderRightWidth={1}
          borderRightColor={colors.border}
          minHeight="100vh"
        >
          {/* Sidebar Header */}
          <YStack
            padding="$4"
            borderBottomWidth={1}
            borderBottomColor={colors.border}
          >
            <XStack justifyContent="space-between" alignItems="center" marginBottom="$3">
              <H2 color={colors.text} fontSize="$6">Community</H2>
              <Button
                size="$3"
                backgroundColor={colors.primary}
                onPress={() => setShowCreateTopic(true)}
                icon={<MaterialIcons name="add" size={16} color="white" />}
              >
                Topic
              </Button>
            </XStack>

            {/* Search */}
            <Input
              placeholder="Search topics..."
              value={searchQuery}
              onChangeText={setSearchQuery}
              backgroundColor={colors.background}
              borderColor={colors.border}
            />
          </YStack>

          {/* Topics List */}
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 8 }}>
            <TopicsList
              topics={searchQuery ? searchResults.topics : topics}
              loading={searchQuery ? searchLoading : loading}
              onTopicSelect={handleTopicSelect}
              selectedTopic={selectedTopic}
              compact
            />
          </ScrollView>
        </YStack>

        {/* Main Content Area */}
        <YStack flex={1}>
          {!selectedTopic ? (
            // Welcome/Getting Started State
            <YStack
              flex={1}
              justifyContent="center"
              alignItems="center"
              padding="$6"
              gap="$4"
            >
              <MaterialCommunityIcons
                name="forum"
                size={80}
                color={colors.textSecondary}
              />
              <H2 color={colors.text} textAlign="center">
                Welcome to the Community
              </H2>
              <SizableText
                color={colors.textSecondary}
                textAlign="center"
                size="$4"
                maxWidth={400}
              >
                Select a topic from the sidebar to view discussions, or create a new topic to start a conversation.
              </SizableText>
              <Button
                size="$4"
                backgroundColor={colors.primary}
                onPress={() => setShowCreateTopic(true)}
                icon={<MaterialIcons name="add" size={20} color="white" />}
              >
                Create First Topic
              </Button>
            </YStack>
          ) : !selectedThread ? (
            // Threads List View
            <YStack flex={1}>
              {/* Threads Header */}
              <XStack
                padding="$4"
                backgroundColor={colors.cardBackground}
                borderBottomWidth={1}
                borderBottomColor={colors.border}
                justifyContent="space-between"
                alignItems="center"
              >
                <H2 color={colors.text}>Discussions</H2>
                <Button
                  size="$3"
                  backgroundColor={colors.success}
                  onPress={() => setShowCreateThread(true)}
                  icon={<MaterialIcons name="add" size={16} color="white" />}
                >
                  New Thread
                </Button>
              </XStack>

              {/* Threads Content */}
              <ScrollView
                style={{ flex: 1 }}
                refreshControl={
                  <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
                }
              >
                <ThreadsList
                  threads={threads}
                  loading={loading}
                  onThreadSelect={handleThreadSelect}
                  selectedThread={selectedThread}
                  topicTitle={topics?.find(t => t._id === selectedTopic)?.title}
                />
              </ScrollView>
            </YStack>
          ) : (
            // Thread Detail View
            <YStack flex={1}>
              {/* Thread Header */}
              <XStack
                padding="$4"
                backgroundColor={colors.cardBackground}
                borderBottomWidth={1}
                borderBottomColor={colors.border}
                alignItems="center"
                gap="$3"
              >
                <Button
                  size="$3"
                  chromeless
                  onPress={handleBackToThreads}
                  icon={<MaterialIcons name="arrow-back" size={20} color={colors.text} />}
                />
                <H2 color={colors.text} flex={1}>
                  {currentThread?.title || 'Loading...'}
                </H2>
              </XStack>

              {/* Thread Content */}
              <ScrollView
                style={{ flex: 1 }}
                refreshControl={
                  <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
                }
              >
                <ThreadDetail
                  thread={currentThread}
                  comments={comments}
                  loading={loading}
                  onBack={handleBackToThreads}
                  onLike={(threadId) => console.log('Like thread:', threadId)}
                  onReply={async (content, parentId) => {
                    try {
                      if (selectedThread) {
                        await communityApi.createComment({
                          content,
                          threadId: selectedThread,
                          parentId,
                        });
                        await loadThread(selectedThread);
                      }
                    } catch (error) {
                      console.error('Error creating comment:', error);
                    }
                  }}
                  currentUserId="currentUser"
                />
              </ScrollView>
            </YStack>
          )}
        </YStack>

        {/* Modals */}
        <CreateTopicModal
          visible={showCreateTopic}
          onClose={() => setShowCreateTopic(false)}
          onSubmit={handleCreateTopic}
        />

        <CreateThreadModal
          visible={showCreateThread}
          onClose={() => setShowCreateThread(false)}
          onSubmit={handleCreateThread}
          topicTitle={topics?.find(t => t._id === selectedTopic)?.title}
        />
      </XStack>
    </ErrorBoundary>
  );
};

export default Community;